import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { defineMetabaseAuthConfig, MetabaseProvider } from '@metabase/embedding-sdk-react'
import CssBaseline from '@mui/material/CssBaseline'
import { Experimental_CssVarsProvider as CssVarsProvider } from '@mui/material/styles'
import { Helmet } from 'react-helmet-async'

import { config } from '@/config'
import { useSettings } from '@/hooks/use-settings'
import { createTheme } from '@/styles/theme/create-theme'
import { getMetabaseDarkTheme, MetabaseLightTheme } from '@/styles/theme/metabase'

const metabaseAuthConfig = defineMetabaseAuthConfig({
	metabaseInstanceUrl: config.metabase?.metabaseInstanceUrl ?? '',
	fetchRequestToken: async () => {
		const url = new URL(config.metabase?.jwtProviderUri ?? '')
		url.searchParams.set('response', 'json')
		url.searchParams.set('metabase_url', config.metabase?.metabaseInstanceUrl ?? '')
		const response = await fetch(url.toString(), { credentials: 'include' })
		return response.json()
	},
})

export interface ThemeProviderProps {
	children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
	const { settings } = useSettings()

	const [metabaseTheme, setMetabaseTheme] = useState(MetabaseLightTheme)

	const theme = createTheme({
		primaryColor: settings.primaryColor,
		colorScheme: settings.colorScheme,
	})

	useEffect(() => {
		// Initialize theme after component mounts
		const MetabaseDarkTheme = getMetabaseDarkTheme()
		const selectedTheme = settings.colorScheme === 'dark' ? MetabaseDarkTheme : MetabaseLightTheme
		setMetabaseTheme(selectedTheme)
	}, [settings.colorScheme])

	return (
		<CssVarsProvider defaultColorScheme={settings.colorScheme} defaultMode={settings.colorScheme} theme={theme}>
			<Helmet>
				<meta content={settings.colorScheme} name="color-scheme" />
				{/* Used for status bar color */}
				<meta content="var(--mui-palette-background-paper)" name="theme-color" />
			</Helmet>
			<MetabaseProvider authConfig={metabaseAuthConfig} theme={metabaseTheme}>
				<CssBaseline />
				{children}
			</MetabaseProvider>
		</CssVarsProvider>
	)
}
